import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { Button, IconLocked, tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';
import { getDateNowByTimezone, isRestaurantOpen } from '@fhs-legacy/frontend/src/utils/restaurant';
import { CART_PLACE_ORDER_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import { usePaymentMethodStore } from '../../../payment';
import { PaymentOrchestrator } from '../../../payment/core/payment-orchestrator';
import { useBuildPipelineContext } from '../../../payment/hooks/use-build-pipeline-context';
import {
  FIRE_SUCCESSFUL,
  ORDER_SUBMITTED_STATUSES,
  type OrderSubmittedStatus,
} from '../../../payment/order-status';
import type { PaymentPipelineError, PaymentPipelineResult } from '../../../payment/payment-types';
import { useCartStore } from '../../../state/cart-store';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';
import { MAX_TIP_AMOUNT_CENTS, useTipStore } from '../../../state/tip-store';
import { logCartPlaceOrder, logPaymentRetry } from '../../../utils/logging-utils';

import {
  type CommitRetryRepriceSession,
  createCommitRetryRepriceSession,
  getCommitRetryRepriceDecision,
  shouldMarkCommitRetryRepriceMutationSettled,
} from './commit-retry-reprice';
import { getPlaceOrderButtonState } from './get-place-order-button-state';
import { handlePlaceOrder } from './handle-place-order';
import { ConfirmingOrderAlert, PaymentErrorAlert, PricingAlert } from './payment-alerts';
import { getDefaultRetryStep, isRetryablePaymentError } from './payment-retry-utils';

/** Swallow a second reprice tap landing before the button disables. */
const PRICING_RETRY_DEBOUNCE_MS = 1000;
const COMMIT_RETRY_REPRICE_TIMEOUT_MS = 90_000;

export default function PlaceOrderButton() {
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentError, setCurrentError] = useState<PaymentPipelineError | null>(null);
  const [pendingCommitRetry, setPendingCommitRetry] = useState(false);
  const lastResultRef = useRef<PaymentPipelineResult | null>(null);
  const orchestratorRef = useRef<PaymentOrchestrator>(new PaymentOrchestrator());
  const commitRetryRepriceSessionRef = useRef<CommitRetryRepriceSession | null>(null);
  const commitRetryRepriceMutationStartedRef = useRef(false);
  const commitRetryRepriceRequestedRef = useRef(false);
  const commitRetrySawPriceRequestedRef = useRef(false);
  const prevPriceMutationLoadingRef = useRef(false);
  const commitRetryPlaceOrderForOrderIdRef = useRef<string | null>(null);
  const [repriceMutationSettled, setRepriceMutationSettled] = useState(false);
  /** Retry press owns reprice → place order so Google Pay runs under that user gesture (web). */
  const commitRetryPressDrivesPaymentRef = useRef(false);
  const commitRetryWaiterRef = useRef<{
    resolve: (rbiOrderId: string) => void;
    reject: (reason: 'reprice_failed' | 'timeout') => void;
  } | null>(null);
  const commitRetryWaitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressInFlightRef = useRef(false);
  const lastPricingRetryAtRef = useRef(0);
  const confirmingStartedRef = useRef(false);

  // Store hooks
  const cartEntries = useCartStore(s => s.cartEntries);
  const isPaymentReady = usePaymentMethodStore(state => state.isPaymentReady());
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const userAccountsLoadError = usePaymentMethodStore(state => state.userAccountsLoadError);
  const setUserAccountsLoadError = usePaymentMethodStore(state => state.setUserAccountsLoadError);
  const setFailedVaulting = usePaymentMethodStore(state => state.setFailedVaulting);
  // Track the previous payment method to detect when user changes it
  const prevPaymentMethodRef = useRef(selectedPaymentMethod);
  const otherTipValue = useTipStore(state => state.otherTipValue);
  const selectedTip = useTipStore(state => state.selectedTip);
  const { buildPipelineContext } = useBuildPipelineContext();
  const {
    setShouldSkipPriceOrder,
    setShouldSkipPriceOrderStatusPolling,
    serverOrderLoading,
    serverOrderError,
    serverOrder,
    priceMutationError,
    priceOrderMutationLoading,
    priceOrderRbiOrderId,
    tryPricing,
  } = useCartContext();
  const { isDelivery } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { isOfferValidationError } = useLoyaltyContext();
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { rewardTierApplied } = useRewardTierApplied();

  const intl = useIntl();
  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  const currentState = usePaymentFlowStore(state => state.currentState);
  const retryInfo = usePaymentFlowStore(state => state.retryInfo);
  const isCommitRetry = usePaymentFlowStore(state => state.isCommitRetry);
  const isOfflineDuringCommit = usePaymentFlowStore(state => state.isOfflineDuringCommit);
  const enterOfflineConfirming = usePaymentFlowStore(state => state.enterOfflineConfirming);
  const setIsOfflineDuringCommit = usePaymentFlowStore(state => state.setIsOfflineDuringCommit);
  const setRbiOrderId = usePaymentFlowStore(state => state.setRbiOrderId);
  const setCurrentState = usePaymentFlowStore(state => state.setCurrentState);
  const setRetryInfo = usePaymentFlowStore(state => state.setRetryInfo);
  const setIsCommitRetry = usePaymentFlowStore(state => state.setIsCommitRetry);
  const { logRBIEvent } = useCRMEventsContext();

  // All Button States
  const {
    isButtonLoading,
    isButtonDisabled,
    buttonText,
    isConfirmingSubmittedOrder,
    hasRetryableFailure,
    isAwaitingPricingRetry,
    needsCommitReprice,
    retryErrorCode,
    isDeliveryFeesLoading,
    hasPricingFailure,
    showPricingAlert,
    showPaymentError,
    confirmingStarted,
  } = getPlaceOrderButtonState({
    serverOrderStatus: serverOrder?.status,
    hasDeliveryQuote: !!serverOrder?.delivery,
    deliveryQuoteStatus: serverOrder?.delivery?.status,
    serverOrderLoading,
    serverOrderError: !!serverOrderError,
    hasPriceMutationError: !!priceMutationError,
    currentState,
    currentErrorCode: currentError?.code,
    hasCurrentError: !!currentError,
    retryInfo,
    isCommitRetry,
    isOfflineDuringCommit,
    pendingCommitRetry,
    isProcessing,
    isDelivery,
    isPaymentReady,
    isOfferValidationError,
    offersLoading,
    cartEntryCount: cartEntries.length,
    hasReachedMaxRedemptionLimit: !!hasReachedMaxRedemptionLimit,
    rewardTierApplied: !!rewardTierApplied,
    isTipAmountInvalid: selectedTip === 'other' && Number(otherTipValue) > MAX_TIP_AMOUNT_CENTS,
    confirmingStarted: confirmingStartedRef.current,
  });

  confirmingStartedRef.current = confirmingStarted;

  // Clear payment error when user changes payment method
  // When Google Pay (or Apple Pay) fails, the error shows and payment method stays the same.
  // When user selects a different payment method, the error clears.
  useEffect(() => {
    if (selectedPaymentMethod !== prevPaymentMethodRef.current) {
      // Payment method changed - clear any existing error
      if (currentError) {
        setCurrentError(null);
        lastResultRef.current = null;
      }
      if (userAccountsLoadError) {
        setUserAccountsLoadError(null);
      }
      prevPaymentMethodRef.current = selectedPaymentMethod;
    }
  }, [selectedPaymentMethod, currentError, userAccountsLoadError, setUserAccountsLoadError]);

  useEffect(() => {
    if (
      currentError &&
      isRetryablePaymentError(currentError.code) &&
      !retryInfo
    ) {
      const retryFromStep = getDefaultRetryStep(currentError.code);
      if (retryFromStep) {
        setRetryInfo({
          canRetry: true,
          retryFromStep,
          errorCode: currentError.code,
          errorMessage: currentError.message,
        });
      }
    }
  }, [currentError, retryInfo, setRetryInfo]);

  useEffect(() => {
    if (
      currentState === 'error' &&
      hasRetryableFailure &&
      !pendingCommitRetry &&
      !isCommitRetry &&
      isProcessing
    ) {
      setIsProcessing(false);
      pressInFlightRef.current = false;
    }
  }, [currentState, hasRetryableFailure, isCommitRetry, pendingCommitRetry, isProcessing]);

  useEffect(() => {
    let lastConnected: boolean | null = null;

    const listener = Network.addListener(status => {
      if (lastConnected === null) {
        lastConnected = status.connected;
        return;
      }
      if (lastConnected === status.connected) {
        return;
      }
      lastConnected = status.connected;

      if (!status.connected) {
        const { currentState: flowState } = usePaymentFlowStore.getState();
        const pipelineActive =
          flowState !== 'idle' && flowState !== 'success' && flowState !== 'error';

        if (isProcessing || pressInFlightRef.current || pipelineActive) {
          enterOfflineConfirming();
        }
        return;
      }

      setIsOfflineDuringCommit(false);
      setShouldSkipPriceOrderStatusPolling(false);

      const { currentState: flowState } = usePaymentFlowStore.getState();
      const awaitingOrderStatus =
        flowState === 'confirming' ||
        flowState === 'insertRequested' ||
        currentError?.code === 'COMMIT_ORDER_UNCONFIRMED';

      if (awaitingOrderStatus) {
        setCurrentState('confirming');
        setIsProcessing(true);
        pressInFlightRef.current = true;
        return;
      }

      if (hasRetryableFailure || currentError) {
        setIsProcessing(false);
        pressInFlightRef.current = false;
      }
    });
    return () => listener.remove();
  }, [
    currentError,
    enterOfflineConfirming,
    hasRetryableFailure,
    isProcessing,
    setCurrentState,
    setIsOfflineDuringCommit,
    setShouldSkipPriceOrderStatusPolling,
  ]);

  const placeOrder = useCallback(async () => {
    try {
      return await handlePlaceOrder({
        lastResultRef,
        orchestratorRef,
        buildPipelineContext,
        setIsProcessing,
        setShouldSkipPriceOrder,
        setCurrentError,
        logRBIEvent,
      });
    } finally {
      pressInFlightRef.current = false;
    }
  }, [buildPipelineContext, setShouldSkipPriceOrder, logRBIEvent]);

  const abandonCommitRetryReprice = useCallback(() => {
    commitRetryRepriceSessionRef.current = null;
    commitRetryPlaceOrderForOrderIdRef.current = null;
    commitRetryRepriceMutationStartedRef.current = false;
    commitRetryRepriceRequestedRef.current = false;
    commitRetrySawPriceRequestedRef.current = false;
    setRepriceMutationSettled(false);
    setPendingCommitRetry(false);
    commitRetryPressDrivesPaymentRef.current = false;
    if (commitRetryWaitTimeoutRef.current) {
      clearTimeout(commitRetryWaitTimeoutRef.current);
      commitRetryWaitTimeoutRef.current = null;
    }
    commitRetryWaiterRef.current = null;
    setIsCommitRetry(false);
    setIsProcessing(false);
    pressInFlightRef.current = false;
  }, [setIsCommitRetry]);

  useEffect(() => {
    if (!pendingCommitRetry) {
      commitRetryRepriceMutationStartedRef.current = false;
      commitRetryRepriceRequestedRef.current = false;
      commitRetrySawPriceRequestedRef.current = false;
      prevPriceMutationLoadingRef.current = false;
      return;
    }

    if (priceOrderMutationLoading) {
      commitRetryRepriceMutationStartedRef.current = true;
    }

    if (serverOrder?.status === 'PRICE_REQUESTED') {
      commitRetrySawPriceRequestedRef.current = true;
      commitRetryRepriceMutationStartedRef.current = true;
    }

    const settleInput = {
      orderIdAtPress: commitRetryRepriceSessionRef.current?.orderIdAtPress ?? null,
      repriceRequested: commitRetryRepriceRequestedRef.current,
      mutationObservedInFlight: commitRetryRepriceMutationStartedRef.current,
      mutationLoading: priceOrderMutationLoading,
      prevMutationLoading: prevPriceMutationLoadingRef.current,
      sawPriceRequested: commitRetrySawPriceRequestedRef.current,
      priceOrderRbiOrderId,
      serverOrderStatus: serverOrder?.status,
      serverOrderRbiOrderId: serverOrder?.rbiOrderId,
    };
    const shouldSettle = shouldMarkCommitRetryRepriceMutationSettled(settleInput);

    if (!repriceMutationSettled && shouldSettle) {
      setRepriceMutationSettled(true);
    }

    prevPriceMutationLoadingRef.current = priceOrderMutationLoading;
  }, [
    pendingCommitRetry,
    priceOrderMutationLoading,
    priceOrderRbiOrderId,
    repriceMutationSettled,
    serverOrder?.rbiOrderId,
    serverOrder?.status,
  ]);

  // Reprice on commit retry, then place order once PriceOrder + GetOrder succeed.
  useEffect(() => {
    if (!pendingCommitRetry) {
      commitRetryRepriceSessionRef.current = null;
      return;
    }

    const decisionInput = {
      pendingCommitRetry,
      session: commitRetryRepriceSessionRef.current,
      repriceMutationSettled,
      priceMutationLoading: priceOrderMutationLoading,
      serverOrderStatus: serverOrder?.status,
      serverOrderRbiOrderId: serverOrder?.rbiOrderId,
      priceOrderRbiOrderId,
      hasPriceMutationError: !!priceMutationError,
      serverOrderError: !!serverOrderError,
    };
    const decision = getCommitRetryRepriceDecision(decisionInput);

    if (decision.kind === 'reprice_failed') {
      commitRetryWaiterRef.current?.reject('reprice_failed');
      commitRetryWaiterRef.current = null;
      abandonCommitRetryReprice();
      return;
    }

    if (decision.kind !== 'run_place_order') {
      return;
    }

    if (commitRetryPressDrivesPaymentRef.current) {
      if (commitRetryWaiterRef.current) {
        if (commitRetryWaitTimeoutRef.current) {
          clearTimeout(commitRetryWaitTimeoutRef.current);
          commitRetryWaitTimeoutRef.current = null;
        }
        commitRetryWaiterRef.current.resolve(decision.rbiOrderId);
        commitRetryWaiterRef.current = null;
      }
      return;
    }

    if (commitRetryPlaceOrderForOrderIdRef.current === decision.rbiOrderId) {
      return;
    }

    commitRetryPlaceOrderForOrderIdRef.current = decision.rbiOrderId;

    void (async () => {
      setRbiOrderId(decision.rbiOrderId);
      const started = await placeOrder();
      commitRetryPlaceOrderForOrderIdRef.current = null;
      if (started) {
        commitRetryRepriceSessionRef.current = null;
        commitRetryRepriceMutationStartedRef.current = false;
        setRepriceMutationSettled(false);
        setPendingCommitRetry(false);
      }
    })();
  }, [
    abandonCommitRetryReprice,
    pendingCommitRetry,
    placeOrder,
    priceMutationError,
    priceOrderRbiOrderId,
    priceOrderMutationLoading,
    repriceMutationSettled,
    serverOrder?.rbiOrderId,
    serverOrder?.status,
    serverOrderError,
    setRbiOrderId,
  ]);

  useEffect(() => {
    const status = serverOrder?.status as OrderSubmittedStatus | undefined;
    const inRecovery =
      currentState === 'insertRequested' ||
      currentState === 'error' ||
      currentState === 'confirming' ||
      isProcessing;

    if (status && inRecovery && ORDER_SUBMITTED_STATUSES.has(status)) {
      // Stay on checkout until network is back and GetOrder shows a terminal success.
      if (
        !isOfflineDuringCommit &&
        (status === 'INSERT_SUCCESSFUL' || status === FIRE_SUCCESSFUL)
      ) {
        if (serverOrder?.rbiOrderId) {
          setRbiOrderId(serverOrder.rbiOrderId);
        }
        setCurrentError(null);
        setRetryInfo(null);
        setCurrentState('success');
        setIsProcessing(false);
        pressInFlightRef.current = false;
        return;
      }

      if (currentState === 'confirming' || currentState === 'error') {
        setCurrentError(null);
        setRetryInfo(null);
        setCurrentState('insertRequested');
      }
      return;
    }

    if (currentState === 'confirming') {
      return;
    }

    if (currentState === 'insertRequested' && serverOrder?.status === 'INSERT_ERROR') {
      setIsProcessing(false);
      setCurrentState('error');
    }
  }, [
    currentState,
    isOfflineDuringCommit,
    serverOrder?.status,
    serverOrder?.rbiOrderId,
    isProcessing,
    retryInfo,
    setCurrentState,
    setRetryInfo,
    setRbiOrderId,
  ]);

  const handleClearError = () => {
    setCurrentError(null);
    lastResultRef.current = null;
  };

  const handleClearUserAccountsLoadError = () => {
    setUserAccountsLoadError(null);
  };

  const userAccountsLoadPipelineError: PaymentPipelineError | null = userAccountsLoadError
    ? {
        code: 'USER_ACCOUNTS_LOAD_FAILED',
        message: userAccountsLoadError,
        userMessage: userAccountsLoadError,
      }
    : null;

  const handlePress = async () => {
    if (pressInFlightRef.current) {
      return;
    }

    // Pricing failed — a reprice is the whole action. Let serverOrderLoading drive
    // the spinner rather than entering the payment pipeline.
    if (isAwaitingPricingRetry) {
      // The button disables on the next render, but a double tap inside that frame
      // would fire a second PriceOrder and trip the backend cooling period. A
      // timestamp self-heals, where holding pressInFlightRef could stick.
      const now = Date.now();
      if (now - lastPricingRetryAtRef.current < PRICING_RETRY_DEBOUNCE_MS) {
        return;
      }
      lastPricingRetryAtRef.current = now;

      logPaymentRetry(logRBIEvent, 'after pricing failure. Retrying pricing');
      setCurrentError(null);
      lastResultRef.current = null;
      setShouldSkipPriceOrder(false);
      setShouldSkipPriceOrderStatusPolling(false);
      tryPricing();
      return;
    }

    pressInFlightRef.current = true;
    setIsProcessing(true);
    setCurrentError(null);
    setFailedVaulting(false);
    // Keep pipeline metadata for payment retries (commit retry + reprice uses fresh wallet auth).
    if (!hasRetryableFailure) {
      lastResultRef.current = null;
    }
    setIsOfflineDuringCommit(false);

    if (isDelivery && store?.number) {

      const fetchedStore = await fetchStore(store.number);
      if (
        fetchedStore &&
        !isRestaurantOpen(
          fetchedStore.deliveryHours,
          getDateNowByTimezone({ timeZone: fetchedStore.timezone || undefined })
        )
      ) {
        setIsProcessing(false);
        pressInFlightRef.current = false;
        return;
      }
    }

    if (hasRetryableFailure) {
      logPaymentRetry(
        logRBIEvent,
        retryInfo
          ? `after failed ${retryInfo.failedStep}. Retrying from ${retryInfo.retryFromStep}`
          : `after payment error ${retryErrorCode}. Retrying checkout`
      );

      if (needsCommitReprice) {
        setIsCommitRetry(true);
        setIsProcessing(true);
        setShouldSkipPriceOrderStatusPolling(false);
        commitRetryRepriceMutationStartedRef.current = false;
        commitRetryRepriceRequestedRef.current = true;
        commitRetrySawPriceRequestedRef.current = false;
        commitRetryPlaceOrderForOrderIdRef.current = null;
        setRepriceMutationSettled(false);
        commitRetryRepriceSessionRef.current = createCommitRetryRepriceSession(
          serverOrder?.rbiOrderId ?? priceOrderRbiOrderId
        );

        commitRetryPressDrivesPaymentRef.current = true;
        const repriceReadyPromise = new Promise<string>((resolve, reject) => {
          commitRetryWaiterRef.current = { resolve, reject };
        });
        commitRetryWaitTimeoutRef.current = setTimeout(() => {
          if (commitRetryWaiterRef.current) {
            commitRetryWaiterRef.current.reject('timeout');
            commitRetryWaiterRef.current = null;
            abandonCommitRetryReprice();
          }
        }, COMMIT_RETRY_REPRICE_TIMEOUT_MS);

        tryPricing();
        setPendingCommitRetry(true);

        try {
          const repricedOrderId = await repriceReadyPromise;
          setRbiOrderId(repricedOrderId);
          await placeOrder();
          commitRetryRepriceSessionRef.current = null;
          commitRetryRepriceMutationStartedRef.current = false;
          setRepriceMutationSettled(false);
          setPendingCommitRetry(false);
        } catch {
          // reprice_failed or timeout — abandonCommitRetryReprice runs from the effect or timer
        } finally {
          commitRetryPressDrivesPaymentRef.current = false;
          if (commitRetryWaitTimeoutRef.current) {
            clearTimeout(commitRetryWaitTimeoutRef.current);
            commitRetryWaitTimeoutRef.current = null;
          }
          commitRetryWaiterRef.current = null;
        }
        return;
      }

      setIsCommitRetry(true);
    } else {
      logCartPlaceOrder(logRBIEvent);
    }

    // set rbiOrderId for routing off cart or checkout page
    if (serverOrder?.rbiOrderId) {
      setRbiOrderId(serverOrder.rbiOrderId);
    }

    void placeOrder();
  };

  return (
    <View style={[styles.container, styles.buttonContainer]}>
      {isConfirmingSubmittedOrder && <ConfirmingOrderAlert />}
      {showPricingAlert && <PricingAlert />}
      {userAccountsLoadPipelineError && (
        <PaymentErrorAlert
          error={userAccountsLoadPipelineError}
          onClear={handleClearUserAccountsLoadError}
        />
      )}
      {showPaymentError && currentError && (
        <PaymentErrorAlert error={currentError} onClear={handleClearError} />
      )}

      <Button
        testID={CART_PLACE_ORDER_BUTTON}
        size="lg"
        loading={isButtonLoading}
        disabled={isButtonDisabled}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={isButtonLoading ? 'Processing order' : buttonText}
        accessibilityHint={
          isAwaitingPricingRetry
            ? 'Retries pricing your order'
            : isButtonDisabled
              ? hasPricingFailure
                ? 'Order cannot be placed due to a pricing error. Please try again'
                : isDeliveryFeesLoading
                  ? 'Please wait while delivery fees are calculated'
                  : 'Please select a payment method and ensure your cart is not empty'
              : 'Places your order and processes payment'
        }
        accessibilityState={{ disabled: isButtonDisabled, busy: isButtonLoading }}
      >
        {!isButtonLoading && !isAwaitingPricingRetry && (
          <Button.Icon>
            <IconLocked />
          </Button.Icon>
        )}
        <Button.Text>{buttonText}</Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  container: {
    marginHorizontal: 'auto',
    padding: 16,
    width: '100%',
  },
});
